import { useState } from "preact/hooks";

import { api } from "@/api/client";
import { PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatEventMetadata } from "@/lib/eventMeta";
import { formatDateTime } from "@/lib/format";
import { currentView, inspectSessionId } from "@/state/filters";

// Jump from an event row straight to the Session Inspector with that session
// pre-loaded — replaces any copy-to-clipboard flow.
function openInInspector(sessionId: string) {
  inspectSessionId.value = sessionId;
  currentView.value = "sessions";
}

const EVENT_TYPES = [
  "",
  "session_started",
  "question_asked",
  "no_answer_found",
  "low_relevance_answer",
  "guardrail_blocked",
  "feedback_given",
  "profession_queried",
  "error_occurred",
  "rag_chunk_sanitized",
];

export function EventsView() {
  const [eventType, setEventType] = useState<string>("");

  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.recentEvents(
        { limit: 200, eventType: eventType || undefined },
        s,
      ),
    [eventType],
    // Poll every 30s — events stream wants tighter cadence than the panels.
    { intervalMs: 30_000 },
  );

  return (
    <div style="display: grid; gap: 16px;">
      <section class="card">
        <div style="display: flex; align-items: center; gap: 16px;">
          <div>
            <span class="muted">{t("events.filter")}&nbsp;</span>
            <select
              value={eventType}
              onChange={(e) => setEventType((e.target as HTMLSelectElement).value)}
              style="padding: 6px 12px; border: 1px solid var(--color-card-border); border-radius: var(--radius-s);"
            >
              {EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type || t("events.allTypes")}
                </option>
              ))}
            </select>
          </div>
          <Staleness
            lastFetched={lastFetched}
            loading={loading}
            error={error}
            onRefresh={refetch}
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("events.title", { n: data?.events.length ?? 0 })}</h2>
        <PanelHelp k="events.overview" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("common.when")}</th>
              <th>{t("common.type")}</th>
              <th>{t("common.session")}</th>
              <th>{t("common.metadata")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.events.map((e) => (
              <tr key={e.event_id}>
                <td class="muted" style="white-space: nowrap;">
                  {formatDateTime(e.timestamp)}
                </td>
                <td>
                  <span class="hl-blue">{e.event_type}</span>
                </td>
                <td style="font-family: var(--font-mono); font-size: 12px;">
                  {e.session_id ? (
                    <button
                      type="button"
                      class="link-mono"
                      title={t("common.openInInspector")}
                      onClick={() => openInInspector(e.session_id)}
                    >
                      {e.session_id.slice(0, 12)}…
                    </button>
                  ) : (
                    "—"
                  )}
                </td>
                <td
                  class="muted"
                  style="font-size: 12px;"
                  // Power users keep the raw JSON on hover; the cell text is
                  // the human-readable summary, rendered inert (no HTML).
                  title={
                    Object.keys(e.metadata).length === 0
                      ? undefined
                      : JSON.stringify(e.metadata)
                  }
                >
                  {formatEventMetadata(e.event_type, e.metadata)}
                </td>
              </tr>
            ))}
            {(!data || data.events.length === 0) && (
              <tr>
                <td colspan={4} class="muted">
                  {t("events.none")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
